import { useState, useCallback, useEffect, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { motion, AnimatePresence } from "framer-motion";
import { Volume2, Mic, MicOff, ArrowLeft, ArrowRight, Star, CheckCircle, XCircle, RotateCcw, Trophy } from "lucide-react";
import { useSpeech } from "@/hooks/useSpeech";
import type { Difficulty } from "./difficulty";
import { getDifficultyConfig } from "./difficulty";

interface PhonicsGameProps {
  onComplete: (result: { score: number; maxScore: number; stars: number }) => void;
  difficulty?: Difficulty;
  startLetter?: string;
}

// ── Letter Data ──
const LETTERS = [
  { letter: "A", sound: "/æ/", word: "Apple", emoji: "🍎", ar: "تفاحة" },
  { letter: "B", sound: "/b/", word: "Ball", emoji: "⚽", ar: "كرة" },
  { letter: "C", sound: "/k/", word: "Cat", emoji: "🐱", ar: "قطة" },
  { letter: "D", sound: "/d/", word: "Dog", emoji: "🐶", ar: "كلب" },
  { letter: "E", sound: "/ɛ/", word: "Egg", emoji: "🥚", ar: "بيضة" },
  { letter: "F", sound: "/f/", word: "Fish", emoji: "🐟", ar: "سمكة" },
  { letter: "G", sound: "/g/", word: "Grape", emoji: "🍇", ar: "عنب" },
  { letter: "H", sound: "/h/", word: "Hat", emoji: "🎩", ar: "قبعة" },
  { letter: "I", sound: "/ɪ/", word: "Ice cream", emoji: "🍦", ar: "آيسكريم" },
  { letter: "J", sound: "/dʒ/", word: "Juice", emoji: "🧃", ar: "عصير" },
  { letter: "K", sound: "/k/", word: "Kite", emoji: "🪁", ar: "طائرة ورقية" },
  { letter: "L", sound: "/l/", word: "Lion", emoji: "🦁", ar: "أسد" },
  { letter: "M", sound: "/m/", word: "Moon", emoji: "🌙", ar: "قمر" },
  { letter: "N", sound: "/n/", word: "Nest", emoji: "🪺", ar: "عش" },
  { letter: "O", sound: "/ɒ/", word: "Orange", emoji: "🍊", ar: "برتقال" },
  { letter: "P", sound: "/p/", word: "Pen", emoji: "🖊️", ar: "قلم" },
  { letter: "Q", sound: "/kw/", word: "Queen", emoji: "👑", ar: "ملكة" },
  { letter: "R", sound: "/r/", word: "Rainbow", emoji: "🌈", ar: "قوس قزح" },
  { letter: "S", sound: "/s/", word: "Sun", emoji: "☀️", ar: "شمس" },
  { letter: "T", sound: "/t/", word: "Tree", emoji: "🌳", ar: "شجرة" },
  { letter: "U", sound: "/ʌ/", word: "Umbrella", emoji: "☂️", ar: "مظلة" },
  { letter: "V", sound: "/v/", word: "Violin", emoji: "🎻", ar: "كمان" },
  { letter: "W", sound: "/w/", word: "Water", emoji: "💧", ar: "ماء" },
  { letter: "X", sound: "/ks/", word: "Box", emoji: "📦", ar: "صندوق" },
  { letter: "Y", sound: "/j/", word: "Yellow", emoji: "💛", ar: "أصفر" },
  { letter: "Z", sound: "/z/", word: "Zebra", emoji: "🦓", ar: "حمار وحشي" },
];

const CVC_WORDS = [
  { word: "cat", letters: ["c", "a", "t"], emoji: "🐱" },
  { word: "dog", letters: ["d", "o", "g"], emoji: "🐶" },
  { word: "sun", letters: ["s", "u", "n"], emoji: "☀️" },
  { word: "hat", letters: ["h", "a", "t"], emoji: "🎩" },
  { word: "pen", letters: ["p", "e", "n"], emoji: "🖊️" },
  { word: "bed", letters: ["b", "e", "d"], emoji: "🛏️" },
  { word: "cup", letters: ["c", "u", "p"], emoji: "☕" },
  { word: "box", letters: ["b", "o", "x"], emoji: "📦" },
  { word: "red", letters: ["r", "e", "d"], emoji: "🔴" },
  { word: "big", letters: ["b", "i", "g"], emoji: "🐘" },
  { word: "map", letters: ["m", "a", "p"], emoji: "🗺️" },
  { word: "bus", letters: ["b", "u", "s"], emoji: "🚌" },
];

type Phase = "learn" | "quiz" | "result";
type QuizType = "sound_match" | "first_letter" | "blending" | "drag_drop";

const shuffle = <T,>(arr: T[]): T[] => [...arr].sort(() => Math.random() - 0.5);
const pick = <T,>(arr: T[], n: number): T[] => shuffle(arr).slice(0, n);

const PhonicsGame = ({ onComplete, difficulty = "medium", startLetter }: PhonicsGameProps) => {
  const config = getDifficultyConfig(difficulty);
  const { speak, stopSpeaking, isSpeaking, encourageCorrect, encourageWrong, startListening, stopListening, isListening, voiceResult, clearVoiceResult } = useSpeech({ lang: "en-US" });

  const [phase, setPhase] = useState<Phase>("learn");
  const [learnIndex, setLearnIndex] = useState(() => {
    if (startLetter) {
      const idx = LETTERS.findIndex(l => l.letter === startLetter.toUpperCase());
      return idx >= 0 ? idx : 0;
    }
    return 0;
  });

  // Quiz state
  const [quizRound, setQuizRound] = useState(0);
  const [score, setScore] = useState(0);
  const [quizQuestions, setQuizQuestions] = useState<any[]>([]);
  const [answered, setAnswered] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [isCorrectAnswer, setIsCorrectAnswer] = useState<boolean | null>(null);

  // Drag & Drop state
  const [dragLetters, setDragLetters] = useState<string[]>([]);
  const [droppedLetters, setDroppedLetters] = useState<string[]>([]);
  const draggedRef = useRef<string | null>(null);

  // Generate quiz questions
  const generateQuizQuestions = useCallback(() => {
    const totalRounds = config.rounds;
    const questions: any[] = [];
    const quizTypes: QuizType[] = ["sound_match", "first_letter", "blending", "drag_drop"];

    for (let i = 0; i < totalRounds; i++) {
      const type = quizTypes[i % quizTypes.length];

      if (type === "sound_match") {
        const correct = LETTERS[Math.floor(Math.random() * LETTERS.length)];
        const others = pick(LETTERS.filter(l => l.letter !== correct.letter), 3);
        const options = shuffle([correct, ...others]);
        questions.push({ type, correct, options, prompt: `Listen to the sound. Which letter is it?` });
      } else if (type === "first_letter") {
        const correct = LETTERS[Math.floor(Math.random() * LETTERS.length)];
        const others = pick(LETTERS.filter(l => l.letter !== correct.letter), 3);
        const options = shuffle([correct, ...others]);
        questions.push({ type, correct, options, prompt: `What letter does "${correct.word}" start with?` });
      } else if (type === "blending") {
        const cvc = CVC_WORDS[Math.floor(Math.random() * CVC_WORDS.length)];
        const otherWords = pick(CVC_WORDS.filter(w => w.word !== cvc.word), 3);
        const options = shuffle([cvc, ...otherWords]);
        questions.push({ type, correct: cvc, options, prompt: `Blend the sounds: ${cvc.letters.join(" - ")}` });
      } else {
        const cvc = CVC_WORDS[Math.floor(Math.random() * CVC_WORDS.length)];
        questions.push({ type, correct: cvc, prompt: `Build the word for ${cvc.emoji}` });
      }
    }
    return questions;
  }, [config.rounds]);

  const startQuiz = useCallback(() => {
    const qs = generateQuizQuestions();
    setQuizQuestions(qs);
    setQuizRound(0);
    setScore(0);
    setAnswered(false);
    setSelectedAnswer(null);
    setIsCorrectAnswer(null);
    setPhase("quiz");
  }, [generateQuizQuestions]);

  // Current learn letter
  const currentLetter = LETTERS[learnIndex];

  // Current quiz question
  const currentQuestion = quizQuestions[quizRound];

  // Init drag letters for drag_drop questions
  useEffect(() => {
    if (phase === "quiz" && currentQuestion?.type === "drag_drop") {
      const cvc = currentQuestion.correct;
      setDragLetters(shuffle([...cvc.letters]));
      setDroppedLetters([]);
    }
  }, [phase, quizRound, currentQuestion]);

  // Handle quiz answer
  const handleAnswer = (answer: string) => {
    if (answered) return;
    setAnswered(true);
    setSelectedAnswer(answer);

    let correct = false;
    if (currentQuestion.type === "sound_match" || currentQuestion.type === "first_letter") {
      correct = answer === currentQuestion.correct.letter;
    } else if (currentQuestion.type === "blending") {
      correct = answer === currentQuestion.correct.word;
    }

    setIsCorrectAnswer(correct);
    if (correct) {
      setScore(s => s + 1);
      encourageCorrect();
    } else {
      encourageWrong();
    }

    setTimeout(() => {
      if (quizRound + 1 >= quizQuestions.length) {
        finishGame(correct ? score + 1 : score);
      } else {
        setQuizRound(r => r + 1);
        setAnswered(false);
        setSelectedAnswer(null);
        setIsCorrectAnswer(null);
      }
    }, 1500);
  };

  // Drag & Drop handlers
  const handleDragStart = (letter: string) => {
    draggedRef.current = letter;
  };

  const handleDrop = () => {
    if (!draggedRef.current) return;
    const letter = draggedRef.current;
    draggedRef.current = null;

    const newDropped = [...droppedLetters, letter];
    setDroppedLetters(newDropped);
    setDragLetters(prev => {
      const idx = prev.indexOf(letter);
      if (idx === -1) return prev;
      return [...prev.slice(0, idx), ...prev.slice(idx + 1)];
    });

    // Check if word is complete
    const cvc = currentQuestion.correct;
    if (newDropped.length === cvc.letters.length) {
      const isCorrect = newDropped.join("") === cvc.letters.join("");
      setAnswered(true);
      setIsCorrectAnswer(isCorrect);
      if (isCorrect) {
        setScore(s => s + 1);
        encourageCorrect();
      } else {
        encourageWrong();
      }
      setTimeout(() => {
        if (quizRound + 1 >= quizQuestions.length) {
          finishGame(isCorrect ? score + 1 : score);
        } else {
          setQuizRound(r => r + 1);
          setAnswered(false);
          setIsCorrectAnswer(null);
        }
      }, 1500);
    }
  };

  const resetDragDrop = () => {
    if (answered) return;
    const cvc = currentQuestion.correct;
    setDragLetters(shuffle([...cvc.letters]));
    setDroppedLetters([]);
  };

  const finishGame = (finalScore: number) => {
    const maxScore = quizQuestions.length;
    const pct = maxScore > 0 ? (finalScore / maxScore) * 100 : 0;
    const stars = pct >= 90 ? 3 : pct >= 60 ? 2 : pct >= 30 ? 1 : 0;
    setPhase("result");
    onComplete({ score: finalScore, maxScore, stars });
  };

  const finalPct = quizQuestions.length > 0 ? Math.round((score / quizQuestions.length) * 100) : 0;
  const finalStars = finalPct >= 90 ? 3 : finalPct >= 60 ? 2 : finalPct >= 30 ? 1 : 0;

  // ── LEARN PHASE ──
  if (phase === "learn") {
    return (
      <div className="max-w-lg mx-auto space-y-4">
        <Card className="overflow-hidden shadow-elevated">
          <CardContent className="p-0">
            <div className="bg-gradient-to-br from-blue-500 to-indigo-600 p-4 text-center">
              <p className="text-white/80 text-sm">Learn the Letter Sound</p>
              <p className="text-white text-xs">{learnIndex + 1} / {LETTERS.length}</p>
            </div>
            <div className="p-8 text-center space-y-4">
              <motion.div
                key={learnIndex}
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="space-y-3"
              >
                <p className="text-8xl font-bold text-primary">{currentLetter.letter}</p>
                <p className="text-6xl">{currentLetter.emoji}</p>
                <p className="text-2xl font-semibold text-foreground">{currentLetter.word}</p>
                <p className="text-muted-foreground text-sm">({currentLetter.ar})</p>
                <Badge variant="secondary" className="text-lg px-4 py-1">
                  Sound: {currentLetter.sound}
                </Badge>
              </motion.div>

              <div className="flex justify-center gap-3 pt-4">
                <Button
                  size="lg"
                  onClick={() => speak(`${currentLetter.letter}. ${currentLetter.letter} says ${currentLetter.word.charAt(0).toLowerCase()}. ${currentLetter.word}. ${currentLetter.letter} is for ${currentLetter.word}`)}
                  disabled={isSpeaking}
                  className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
                >
                  <Volume2 className="w-5 h-5 mr-2" />
                  Listen 🔊
                </Button>
                <Button
                  size="lg"
                  variant={isListening ? "destructive" : "outline"}
                  onClick={isListening ? stopListening : startListening}
                >
                  {isListening ? <MicOff className="w-5 h-5 mr-2" /> : <Mic className="w-5 h-5 mr-2" />}
                  {isListening ? "Stop" : "Record 🎙️"}
                </Button>
              </div>

              {voiceResult && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                  <Badge
                    variant={voiceResult.toLowerCase().includes(currentLetter.letter.toLowerCase()) ? "default" : "destructive"}
                    className="text-base px-4 py-2"
                  >
                    {voiceResult.toLowerCase().includes(currentLetter.letter.toLowerCase())
                      ? `✅ Great! You said: "${voiceResult}"`
                      : `❌ You said: "${voiceResult}" — Try again!`}
                  </Badge>
                </motion.div>
              )}
            </div>

            <div className="p-4 border-t flex justify-between">
              <Button
                variant="outline"
                onClick={() => { clearVoiceResult(); setLearnIndex(i => Math.max(0, i - 1)); }}
                disabled={learnIndex === 0}
              >
                <ArrowLeft className="w-4 h-4 mr-1" /> Previous
              </Button>
              {learnIndex < LETTERS.length - 1 ? (
                <Button
                  onClick={() => { clearVoiceResult(); setLearnIndex(i => i + 1); }}
                >
                  Next <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button
                  onClick={startQuiz}
                  className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                >
                  Start Quiz! 🎯
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Skip to quiz */}
        <div className="text-center">
          <Button variant="ghost" size="sm" onClick={startQuiz} className="text-muted-foreground">
            Skip to Quiz →
          </Button>
        </div>
      </div>
    );
  }

  // ── QUIZ PHASE ──
  if (phase === "quiz" && currentQuestion) {
    const progress = ((quizRound + 1) / quizQuestions.length) * 100;

    return (
      <div className="max-w-lg mx-auto space-y-4">
        {/* Progress */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>Question {quizRound + 1} / {quizQuestions.length}</span>
            <span>Score: {score} ⭐</span>
          </div>
          <Progress value={progress} className="h-3" />
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={quizRound}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
          >
            <Card className="overflow-hidden shadow-elevated">
              <CardContent className="p-0">
                {/* Sound Match */}
                {currentQuestion.type === "sound_match" && (
                  <>
                    <div className="bg-gradient-to-br from-violet-500 to-purple-600 p-6 text-center text-white">
                      <p className="text-sm opacity-80 mb-2">🔊 Listen & Match</p>
                      <Button
                        size="lg"
                        variant="secondary"
                        onClick={() => speak(`${currentQuestion.correct.letter}. ${currentQuestion.correct.letter} says ${currentQuestion.correct.word.charAt(0).toLowerCase()}`)}
                        disabled={isSpeaking}
                        className="bg-white/20 hover:bg-white/30 text-white border-0"
                      >
                        <Volume2 className="w-6 h-6 mr-2" /> Play Sound
                      </Button>
                    </div>
                    <div className="p-6 grid grid-cols-2 gap-3">
                      {currentQuestion.options.map((opt: any) => (
                        <motion.button
                          key={opt.letter}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          disabled={answered}
                          onClick={() => handleAnswer(opt.letter)}
                          className={`p-6 rounded-xl border-2 text-center transition-all ${
                            answered && opt.letter === currentQuestion.correct.letter
                              ? "border-green-500 bg-green-50 dark:bg-green-950"
                              : answered && selectedAnswer === opt.letter && !isCorrectAnswer
                              ? "border-red-500 bg-red-50 dark:bg-red-950"
                              : "border-border hover:border-primary/50 bg-card"
                          }`}
                        >
                          <p className="text-4xl font-bold text-foreground">{opt.letter}</p>
                          <p className="text-2xl mt-1">{opt.emoji}</p>
                        </motion.button>
                      ))}
                    </div>
                  </>
                )}

                {/* First Letter */}
                {currentQuestion.type === "first_letter" && (
                  <>
                    <div className="bg-gradient-to-br from-amber-500 to-orange-600 p-6 text-center text-white">
                      <p className="text-sm opacity-80 mb-2">🖼️ What's the first letter?</p>
                      <p className="text-6xl mb-2">{currentQuestion.correct.emoji}</p>
                      <p className="text-xl font-bold">{currentQuestion.correct.word}</p>
                    </div>
                    <div className="p-6 grid grid-cols-2 gap-3">
                      {currentQuestion.options.map((opt: any) => (
                        <motion.button
                          key={opt.letter}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          disabled={answered}
                          onClick={() => handleAnswer(opt.letter)}
                          className={`p-6 rounded-xl border-2 text-center transition-all ${
                            answered && opt.letter === currentQuestion.correct.letter
                              ? "border-green-500 bg-green-50 dark:bg-green-950"
                              : answered && selectedAnswer === opt.letter && !isCorrectAnswer
                              ? "border-red-500 bg-red-50 dark:bg-red-950"
                              : "border-border hover:border-primary/50 bg-card"
                          }`}
                        >
                          <p className="text-4xl font-bold text-foreground">{opt.letter}</p>
                        </motion.button>
                      ))}
                    </div>
                  </>
                )}

                {/* Blending */}
                {currentQuestion.type === "blending" && (
                  <>
                    <div className="bg-gradient-to-br from-teal-500 to-cyan-600 p-6 text-center text-white">
                      <p className="text-sm opacity-80 mb-2">🔤 Blend the Sounds!</p>
                      <div className="flex justify-center gap-2 items-center">
                        {currentQuestion.correct.letters.map((l: string, i: number) => (
                          <span key={i}>
                            <span className="text-3xl font-bold bg-white/20 px-3 py-1 rounded-lg">{l}</span>
                            {i < currentQuestion.correct.letters.length - 1 && <span className="text-2xl mx-1">+</span>}
                          </span>
                        ))}
                      </div>
                      <Button
                        size="sm"
                        variant="secondary"
                        className="mt-3 bg-white/20 hover:bg-white/30 text-white border-0"
                        onClick={() => speak(currentQuestion.correct.letters.join(". ") + ". " + currentQuestion.correct.word)}
                        disabled={isSpeaking}
                      >
                        <Volume2 className="w-4 h-4 mr-1" /> Listen
                      </Button>
                    </div>
                    <div className="p-6 grid grid-cols-2 gap-3">
                      {currentQuestion.options.map((opt: any) => (
                        <motion.button
                          key={opt.word}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          disabled={answered}
                          onClick={() => handleAnswer(opt.word)}
                          className={`p-5 rounded-xl border-2 text-center transition-all ${
                            answered && opt.word === currentQuestion.correct.word
                              ? "border-green-500 bg-green-50 dark:bg-green-950"
                              : answered && selectedAnswer === opt.word && !isCorrectAnswer
                              ? "border-red-500 bg-red-50 dark:bg-red-950"
                              : "border-border hover:border-primary/50 bg-card"
                          }`}
                        >
                          <p className="text-2xl">{opt.emoji}</p>
                          <p className="text-xl font-bold text-foreground mt-1">{opt.word}</p>
                        </motion.button>
                      ))}
                    </div>
                  </>
                )}

                {/* Drag & Drop */}
                {currentQuestion.type === "drag_drop" && (
                  <>
                    <div className="bg-gradient-to-br from-pink-500 to-rose-600 p-6 text-center text-white">
                      <p className="text-sm opacity-80 mb-2">🧩 Build the Word!</p>
                      <p className="text-5xl mb-2">{currentQuestion.correct.emoji}</p>
                      <Button
                        size="sm"
                        variant="secondary"
                        className="bg-white/20 hover:bg-white/30 text-white border-0"
                        onClick={() => speak(currentQuestion.correct.word)}
                        disabled={isSpeaking}
                      >
                        <Volume2 className="w-4 h-4 mr-1" /> Listen
                      </Button>
                    </div>
                    <div className="p-6 space-y-4">
                      {/* Drop zone */}
                      <div
                        className={`flex justify-center gap-2 min-h-[70px] p-4 border-2 border-dashed rounded-xl transition-colors ${
                          answered
                            ? isCorrectAnswer
                              ? "border-green-500 bg-green-50 dark:bg-green-950"
                              : "border-red-500 bg-red-50 dark:bg-red-950"
                            : "border-primary/30 bg-muted/50"
                        }`}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={handleDrop}
                      >
                        {droppedLetters.length === 0 && (
                          <p className="text-muted-foreground text-sm self-center">Drag letters here</p>
                        )}
                        {droppedLetters.map((l, i) => (
                          <motion.span
                            key={i}
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="text-3xl font-bold bg-primary text-primary-foreground w-12 h-12 rounded-lg flex items-center justify-center"
                          >
                            {l}
                          </motion.span>
                        ))}
                      </div>

                      {/* Draggable letters */}
                      <div className="flex justify-center gap-3">
                        {dragLetters.map((l, i) => (
                          <motion.div
                            key={`${l}-${i}`}
                            draggable={!answered}
                            onDragStart={() => handleDragStart(l)}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            className="text-3xl font-bold bg-secondary text-secondary-foreground w-14 h-14 rounded-xl flex items-center justify-center cursor-grab active:cursor-grabbing border-2 border-border shadow-sm"
                          >
                            {l}
                          </motion.div>
                        ))}
                      </div>

                      {!answered && droppedLetters.length > 0 && (
                        <div className="text-center">
                          <Button variant="ghost" size="sm" onClick={resetDragDrop}>
                            <RotateCcw className="w-4 h-4 mr-1" /> Reset
                          </Button>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Answer feedback */}
                {answered && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className={`p-3 text-center text-white ${isCorrectAnswer ? "bg-green-500" : "bg-red-500"}`}
                  >
                    {isCorrectAnswer ? (
                      <span className="flex items-center justify-center gap-2"><CheckCircle className="w-5 h-5" /> Correct! 🎉</span>
                    ) : (
                      <span className="flex items-center justify-center gap-2"><XCircle className="w-5 h-5" /> Try next time!</span>
                    )}
                  </motion.div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </AnimatePresence>
      </div>
    );
  }

  // ── RESULT PHASE ──
  if (phase === "result") {
    return (
      <div className="max-w-md mx-auto">
        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
          <Card className="overflow-hidden shadow-elevated">
            <CardContent className="p-0">
              <div className="bg-gradient-to-br from-amber-400 to-orange-500 p-8 text-center text-white">
                <Trophy className="w-16 h-16 mx-auto mb-3" />
                <h2 className="text-2xl font-bold">Phonics Complete!</h2>
                <p className="text-lg opacity-90">اكتمل درس الفونكس!</p>
              </div>
              <div className="p-8 text-center space-y-4">
                <div className="flex justify-center gap-2">
                  {[1, 2, 3].map(s => (
                    <Star
                      key={s}
                      className={`w-10 h-10 ${s <= finalStars ? "text-amber-400 fill-amber-400" : "text-muted"}`}
                    />
                  ))}
                </div>
                <p className="text-4xl font-bold text-foreground">{finalPct}%</p>
                <p className="text-muted-foreground">
                  {score} / {quizQuestions.length} correct
                </p>
                <div className="flex justify-center gap-3 pt-4">
                  <Button onClick={() => { setPhase("learn"); setLearnIndex(0); }} variant="outline">
                    <RotateCcw className="w-4 h-4 mr-2" /> Play Again
                  </Button>
                  <Button onClick={startQuiz} className="bg-gradient-to-r from-blue-500 to-indigo-600">
                    Quiz Only 🎯
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return null;
};

export default PhonicsGame;
